import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent as RPointerEvent } from 'react';
import { ImagePlus, Loader2, Minus, Plus, RotateCcw, Maximize, Minimize, Check, Upload } from 'lucide-react';
import { MAX_ZOOM, minZoom, photoBox, type Crop, type Element } from '@/lib/brand-kit';
import { cn } from '@/lib/cn';

/** Click a photo frame on the design itself to fill it, then move and zoom the photo inside
 *  (Aziz 2026-10-23). The overlay only steers: the canvas draws the photo clipped to the frame
 *  shape with the frame's stroke on top, through the same photoBox() the export uses. */
/** has = a photo is in; adjust = it can be moved and zoomed (a cover frame whose photo has drawn) */
export interface Frame { el: Element; label: string; has: boolean; adjust: boolean; busy: boolean }
export const NO_CROP: Crop = { zoom: 1, x: 0, y: 0 };
/** a frame's starting placement: the template's default_crop, else centred and filling */
export const startCrop = (el: Pick<Element, 'default_crop'>): Crop => el.default_crop ?? NO_CROP;
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

/** A new zoom that keeps the point at the frame's centre where it is. */
export function zoomTo(el: Element, size: { w: number; h: number } | undefined, c: Crop, zoom: number): Crop {
  const z = clamp(zoom, minZoom(el), MAX_ZOOM);
  if (!size) return { ...c, zoom: z };
  const a = photoBox(el, size.w, size.h, c), b = photoBox(el, size.w, size.h, { ...c, zoom: z, x: 0, y: 0 });
  // below 'fill' (a shrink frame) the photo moves inside the free space instead of the overflow
  const nx = Math.abs(b.dw - el.w) > 0.5 ? clamp((a.ox / a.dw) * b.dw * 2 / (b.dw - el.w), -1, 1) : 0;
  const ny = Math.abs(b.dh - el.h) > 0.5 ? clamp((a.oy / a.dh) * b.dh * 2 / (b.dh - el.h), -1, 1) : 0;
  return { zoom: z, x: nx, y: ny };
}
/** Move the photo by (dx, dy) design pixels: it follows the finger, never past a frame edge. */
export function moveBy(el: Element, size: { w: number; h: number } | undefined, c: Crop, dx: number, dy: number): Crop {
  if (!size) return c;
  const b = photoBox(el, size.w, size.h, c);
  return {
    ...c,
    x: Math.abs(b.dw - el.w) > 0.5 ? clamp((c.x || 0) - (dx * 2) / (b.dw - el.w), -1, 1) : 0,
    y: Math.abs(b.dh - el.h) > 0.5 ? clamp((c.y || 0) - (dy * 2) / (b.dh - el.h), -1, 1) : 0,
  };
}

export default function PhotoFrames({ w, h, frames, crops, sizes, selected, onSelect, onPick, onCrop }: {
  w: number; h: number; frames: Frame[]; crops: Record<string, Crop>; sizes: Record<string, { w: number; h: number }>;
  selected: string | null; onSelect: (id: string | null) => void;
  onPick: (id: string, f: File) => void; onCrop: (id: string, c: Crop) => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const file = useRef<HTMLInputElement>(null);
  const target = useRef('');
  const pts = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef<{ crop: Crop; dist: number; x: number; y: number } | null>(null);
  const live = useRef({ crops, sizes, frames, selected, onCrop });
  live.current = { crops, sizes, frames, selected, onCrop };
  const [hover, setHover] = useState<string | null>(null);

  const open = (id: string) => { target.current = id; file.current?.click(); };
  const unit = () => w / (box.current?.clientWidth || w);   // design pixels per screen pixel

  // the wheel zooms the selected photo; React's wheel handler is passive and cannot stop the page scroll
  useEffect(() => {
    const node = box.current; if (!node) return;
    const wheel = (ev: WheelEvent) => {
      const { selected: id, frames: fs, crops: cs, sizes: ss, onCrop: set } = live.current;
      const f = fs.find((x) => x.el.id === id && x.adjust);
      if (!f || !(ev.target as HTMLElement).closest(`[data-frame="${f.el.id}"]`)) return;
      ev.preventDefault();
      const c = cs[f.el.id] ?? NO_CROP;
      set(f.el.id, zoomTo(f.el, ss[f.el.id], c, (c.zoom || 1) * Math.exp(-ev.deltaY * 0.0015)));
    };
    node.addEventListener('wheel', wheel, { passive: false });
    return () => node.removeEventListener('wheel', wheel);
  }, []);

  const down = (ev: RPointerEvent<HTMLDivElement>, f: Frame) => {
    if (!f.adjust || f.busy) return;
    if (selected !== f.el.id) onSelect(f.el.id);
    (ev.currentTarget as HTMLDivElement).setPointerCapture(ev.pointerId);
    pts.current.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
    startGesture(f);
  };
  const startGesture = (f: Frame) => {
    const p = [...pts.current.values()];
    const cx = p.reduce((s, q) => s + q.x, 0) / p.length, cy = p.reduce((s, q) => s + q.y, 0) / p.length;
    const dist = p.length > 1 ? Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y) : 0;
    gesture.current = { crop: crops[f.el.id] ?? NO_CROP, dist, x: cx, y: cy };
  };
  const move = (ev: RPointerEvent<HTMLDivElement>, f: Frame) => {
    if (!pts.current.has(ev.pointerId) || !gesture.current) return;
    pts.current.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
    const p = [...pts.current.values()], g = gesture.current;
    const cx = p.reduce((s, q) => s + q.x, 0) / p.length, cy = p.reduce((s, q) => s + q.y, 0) / p.length;
    let c = g.crop;
    if (p.length > 1 && g.dist > 0) c = zoomTo(f.el, sizes[f.el.id], c, (c.zoom || 1) * Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y) / g.dist);
    c = moveBy(f.el, sizes[f.el.id], c, (cx - g.x) * unit(), (cy - g.y) * unit());
    onCrop(f.el.id, c);
  };
  const up = (ev: RPointerEvent<HTMLDivElement>, f: Frame) => {
    pts.current.delete(ev.pointerId);
    if (pts.current.size) startGesture(f); else gesture.current = null;
  };
  const key = (ev: KeyboardEvent<HTMLDivElement>, f: Frame) => {
    if (ev.target !== ev.currentTarget) return;
    if (ev.key === 'Enter' || ev.key === ' ') {
      ev.preventDefault();
      if (!f.adjust) open(f.el.id); else onSelect(selected === f.el.id ? null : f.el.id);
      return;
    }
    if (selected !== f.el.id || !f.adjust) return;
    const c = crops[f.el.id] ?? NO_CROP, step = 20, s = sizes[f.el.id];
    const set = (n: Crop) => { ev.preventDefault(); onCrop(f.el.id, n); };
    if (ev.key === 'ArrowLeft') set(moveBy(f.el, s, c, -step, 0));
    else if (ev.key === 'ArrowRight') set(moveBy(f.el, s, c, step, 0));
    else if (ev.key === 'ArrowUp') set(moveBy(f.el, s, c, 0, -step));
    else if (ev.key === 'ArrowDown') set(moveBy(f.el, s, c, 0, step));
    else if (ev.key === '+' || ev.key === '=') set(zoomTo(f.el, s, c, (c.zoom || 1) + 0.25));
    else if (ev.key === '-') set(zoomTo(f.el, s, c, (c.zoom || 1) - 0.25));
    else if (ev.key === 'Escape') { ev.preventDefault(); onSelect(null); }
  };

  return (
    <div ref={box} className="absolute left-0 top-0 w-full select-none" style={{ aspectRatio: `${w} / ${h}` }}
      onPointerDown={(ev) => { if (ev.target === ev.currentTarget) onSelect(null); }} data-testid="photo-frames">
      <input ref={file} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="frame-file"
        onChange={(ev) => { const f = ev.target.files?.[0]; if (f && target.current) onPick(target.current, f); ev.target.value = ''; }} />
      {frames.map((f) => {
        const { el } = f, on = selected === el.id, show = hover === el.id;
        const rr = Math.min(el.radius, Math.min(el.w, el.h) / 2);
        return (
          <div key={el.id} data-frame={el.id} data-testid={`frame-${el.role}`} role="button" tabIndex={0}
            aria-label={f.adjust ? `${f.label}: select to move and zoom, or replace` : f.has ? `${f.label}: replace the photo` : `${f.label}: upload a photo`}
            aria-pressed={f.adjust ? on : undefined}
            data-zoom={(crops[el.id] ?? NO_CROP).zoom} data-ox={(crops[el.id] ?? NO_CROP).x} data-oy={(crops[el.id] ?? NO_CROP).y}
            className={cn('absolute outline-none focus-visible:ring-2 focus-visible:ring-accent-400',
              f.adjust && on ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer')}
            style={{ left: `${(el.x / w) * 100}%`, top: `${(el.y / h) * 100}%`, width: `${(el.w / w) * 100}%`, height: `${(el.h / h) * 100}%`,
              borderRadius: rr ? `${(rr / el.w) * 100}% / ${(rr / el.h) * 100}%` : undefined,
              touchAction: on ? 'none' : 'auto', boxShadow: on ? '0 0 0 3px rgb(56 189 248)' : show || !f.adjust ? '0 0 0 2px rgba(56,189,248,0.8)' : undefined }}
            onPointerEnter={() => setHover(el.id)} onPointerLeave={() => setHover((x) => (x === el.id ? null : x))}
            onClick={() => { if (!f.adjust && !f.busy) open(el.id); }}
            onPointerDown={(ev) => down(ev, f)} onPointerMove={(ev) => move(ev, f)}
            onPointerUp={(ev) => up(ev, f)} onPointerCancel={(ev) => up(ev, f)} onKeyDown={(ev) => key(ev, f)}>
            {f.busy ? (
              <span className="absolute inset-0 grid place-items-center pointer-events-none">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-black/70 px-3 py-1.5 text-xs text-white"><Loader2 size={14} className="animate-spin" /> Uploading...</span>
              </span>
            ) : !f.adjust || (show && !on) ? (
              <span className="absolute inset-0 grid place-items-center pointer-events-none">
                <button type="button" tabIndex={-1} data-testid={`frame-upload-${el.role}`}
                  className={cn('pointer-events-auto inline-flex items-center gap-1.5 rounded-full bg-black/70 px-3 py-1.5 text-xs text-white shadow hover:bg-black/85',
                    !f.adjust && !show && 'opacity-70')}
                  onPointerDown={(ev) => ev.stopPropagation()}
                  onClick={(ev) => { ev.stopPropagation(); open(el.id); }}>
                  {f.has ? <Upload size={14} /> : <ImagePlus size={14} />} {f.has ? 'Replace photo' : 'Upload photo'}
                </button>
              </span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/** Zoom, Fit and Reset for the selected frame, under the design so it never covers it. */
export function FrameToolbar({ frame, crop, size, linked, onCrop, onReplace, onDone }: {
  frame: Frame; crop: Crop; size?: { w: number; h: number }; linked: boolean;
  onCrop: (c: Crop) => void; onReplace: () => void; onDone: () => void;
}) {
  const el = frame.el, z = crop.zoom || 1, zMin = minZoom(el);
  // the zoom at which the whole photo fits inside the frame (shrink frames only)
  const whole = size ? clamp(Math.min(el.w / size.w, el.h / size.h) / Math.max(el.w / size.w, el.h / size.h), zMin, 1) : 1;
  const btn = 'inline-flex items-center gap-1 h-8 px-2.5 rounded-md border border-slate-700 text-xs text-muted-strong hover:bg-white/5 disabled:opacity-40';
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="frame-toolbar">
      <span className="text-xs text-muted-strong mr-1">{frame.label}{linked ? ' (both photos)' : ''}: drag to move, wheel or pinch to zoom</span>
      <button type="button" className={btn} aria-label="Zoom out" disabled={z <= zMin} onClick={() => onCrop(zoomTo(el, size, crop, z - 0.25))} data-testid="frame-zoom-out"><Minus size={14} /></button>
      <input type="range" min={zMin} max={MAX_ZOOM} step={0.01} value={z} aria-label="Zoom" className="w-32"
        onChange={(e) => onCrop(zoomTo(el, size, crop, Number(e.target.value)))} data-testid="frame-zoom" />
      <button type="button" className={btn} aria-label="Zoom in" disabled={z >= MAX_ZOOM} onClick={() => onCrop(zoomTo(el, size, crop, z + 0.25))} data-testid="frame-zoom-in"><Plus size={14} /></button>
      <span className="text-xs text-muted tabular-nums w-10" data-testid="frame-zoom-value">{z.toFixed(1)}x</span>
      <button type="button" className={btn} title="Fill the frame at the smallest size, keeping the spot you chose" onClick={() => onCrop({ ...crop, zoom: 1 })} data-testid="frame-fit"><Maximize size={14} /> Fit</button>
      {el.shrink && <button type="button" className={btn} title="Show the whole photo inside the frame, centred" onClick={() => onCrop({ zoom: whole, x: 0, y: 0 })} data-testid="frame-whole"><Minimize size={14} /> Whole photo</button>}
      <button type="button" className={btn} title="Back to the template's own placement" onClick={() => onCrop(startCrop(el))} data-testid="frame-reset"><RotateCcw size={14} /> Reset</button>
      <button type="button" className={btn} onClick={onReplace} data-testid="frame-replace"><Upload size={14} /> Replace</button>
      <button type="button" className={btn} onClick={onDone} data-testid="frame-done"><Check size={14} /> Done</button>
    </div>
  );
}
